import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { X } from 'lucide-react';

const defaultIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

/**
 * Confirm-before-a-consequential-action dialog. Defaults reproduce the original
 * discard-changes copy, so existing callers only pass isOpen/onCancel/onConfirm.
 */
export default function DiscardChangesModal({
  isOpen,
  title = 'Discard changes?',
  message = 'Your unsaved changes will be lost if you leave this page.',
  cancelLabel = 'Keep editing',
  confirmLabel = 'Discard & leave',
  onCancel,
  onConfirm,
  confirmVariant = 'outline',
  icon,
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (!isOpen) return null;

  const outlineCls = `${isDark ? 'border-btn-dark' : 'border-btn !text-textPrimary'} hover:scale-105 transition-transform duration-200 !py-1.5 !px-7 mt-3`;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[99999999]">

      <div
        className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
        style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
        onMouseDown={onCancel}
      />

      <div
        className="w-full max-w-md p-6 rounded-lg shadow-lg drop-shadow-sm relative bg-bgSecondary"
        style={{
          background: isDark
            ? 'linear-gradient(to bottom right, rgba(34,52,97,0.25), rgba(11,10,31,0.05))'
            : '',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(50px)',
          borderRadius: '10px',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              {icon ?? defaultIcon}
            </div>
            <h3 className="dark:text-[#C1A0D0] text-textPrimary text-left text-lg">
              {title}
            </h3>
          </div>
          <button onClick={onCancel}>
            <X className="h-5 w-5 dark:text-[#C1A0D0] text-textPrimary" />
          </button>
        </div>

        {/* Body */}
        <p className="text-lg dark:text-[#CCCCCC] text-textSecondary leading-relaxed mt-10 mb-8">
          {message}
        </p>

        {/* Buttons*/}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className={outlineCls}>
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={
              confirmVariant === 'solid'
                ? 'hover:scale-105 transition-transform duration-200 !py-1.5 !px-7 mt-3 rounded-full text-sm font-semibold text-white'
                : outlineCls
            }
            style={
              confirmVariant === 'solid'
                ? { background: 'linear-gradient(to right, #ec4899, #3b82f6)' }
                : undefined
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
